import { useState, useEffect } from 'react';
import paymentIcon from "../assets/Group.svg";
import "../styles/PaymentPage.css";
import { getPaymentMethods, makeDefaultPaymentMethod, createSetupIntent, createSubscription, deletePaymentMethod } from '../services/PaymentService';
import AddPaymentMethod from './AddPaymentMethod';
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import visaIcon from "../assets/visa.svg";
import masterCardIcon from "../assets/mastercard.svg";
import amexIcon from "../assets/amex.svg";
import discoverIcon from "../assets/discover.svg";
import unknownCardIcon from "../assets/unknown_card.svg"
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, MenuItem, IconButton } from "@mui/material";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {updatePlanTierId, updateCurrentPlanStartDate, updateStripeCustomerId, updateEmailId, updateFirstName, updateIsAuthenticated, updateLastName, updatePhoneNumber, updateUserId} from '../store/userDetailsSlice';
import { useDispatch } from 'react-redux';
import { allowAccessToPaymentStatusPage } from "../store/navigationSlice";
import { updateUserDetails, updatePlan } from "../services/PersonalDetailsApi";
import { useSelector } from "react-redux";
import { first, set } from 'lodash';

const brandIcons = {
    visa: visaIcon,
    mastercard: masterCardIcon,
    amex: amexIcon,
    discover: discoverIcon,
    unknown: unknownCardIcon
};

const PaymentPage = () => {

    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const itemPrice = location.state?.itemPrice;
    const creditsUsed = location.state?.creditsUsed;
    const total = location.state?.total;
    const emailId = location.state?.emailId;
    const planTierId = location.state?.planTierId;
    const isFirstTimeSubscriber = location.state?.isFirstTimeSubscriber || false;
    const stripePriceId = location.state?.stripePriceId;

    const { stripeCustomerId: stripeCustomerId = ''} = useSelector((state) => state.userDetails);

    const [paymentMethods, setPaymentMethods] = useState([]);
    const [isPaymentMethodLoading, setIsPaymentMethodLoading] = useState(true);
    const [noPaymentMethodsAdded, setNoPaymentMethodsAdded] = useState(false);
    const [isPaymentLoading, setIsPaymentLoading] = useState(false);
    const [selectedCardId, setSelectedCardId] = useState(null);
    const [showAddCard, setShowAddCard] = useState(false);
    const [setupClientSecret, setSetupClientSecret] = useState(null);
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
    const [menuOpenFor, setMenuOpenFor] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleMenuOpen = (event, pmId) => {
        setAnchorEl(event.currentTarget);
        setMenuOpenFor(pmId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuOpenFor(null);
    };

    // Load saved cards on mount
    useEffect(() => {
        loadPaymentMethods();
    }, []);

    const loadPaymentMethods = async () => {
        try {
            setIsPaymentMethodLoading(true); // Set loading state to true
            const data = await getPaymentMethods(stripeCustomerId); // Wait for the API call to complete

            if (data.paymentMethods && data.paymentMethods.length === 0) {
                setNoPaymentMethodsAdded(true);
            } else {
                setPaymentMethods(data.paymentMethods);
                setSelectedCardId(data.defaultPaymentMethodId);
                setDefaultPaymentMethod(data.defaultPaymentMethodId);
                setNoPaymentMethodsAdded(false);
            }
        } catch (error) {
            console.error("Error fetching payment methods:", error);
        } finally {
            setIsPaymentMethodLoading(false); // Set loading state to false
        }
    };

    const makeDefaultPayment = async(paymentMethodId) => {
        await makeDefaultPaymentMethod(stripeCustomerId, paymentMethodId);
        handleMenuClose();
        setSelectedCardId(paymentMethodId);
        setDefaultPaymentMethod(paymentMethodId);
    }

    const addPaymentMethod = async() => {
        const data = await createSetupIntent(stripeCustomerId);
        if (data.clientSecret) {
            setShowAddCard(true);
            setSetupClientSecret(data.clientSecret);
        }
    }

    const handleAddingCardComplete = async (newPaymentMethodId) => {
        setShowAddCard(false);
        
        // Refresh the list of saved payment methods
        await loadPaymentMethods();

        setSelectedCardId(newPaymentMethodId);
    };

    const handleSubscribe = async () => {
        try {
            setIsPaymentLoading(true);
            const subscriptionResponse = await createSubscription(stripeCustomerId, stripePriceId, selectedCardId, creditsUsed, itemPrice);
            if (subscriptionResponse.error) {
                console.error("Subscription error:", subscriptionResponse.error);
                dispatch(allowAccessToPaymentStatusPage());
                navigate('/payment-failure', {
                    state: {
                      errorMessage: subscriptionResponse.error,
                    },
                });
                return;
            }

            // If a PaymentIntent was created and requires confirmation:
            if (subscriptionResponse.data.clientSecret) {
                const stripe = await stripePromise;
                const { error } = await stripe.confirmCardPayment(subscriptionResponse.data.clientSecret);
                if (error) {
                    console.error("Payment confirmation error:", error.message);
                    dispatch(allowAccessToPaymentStatusPage());
                    navigate('/payment-failure', {
                        state: {
                          errorMessage: error.message,
                        },
                    });
                } else {
                    updatePlan(emailId, planTierId);
                    if (isFirstTimeSubscriber) {
                        const updates = {
                            isRegistrationComplete: true,
                        };
                        const response = await updateUserDetails(emailId, updates);
                        if (response.success) {
                            const userDetails = response.data;
                            dispatch(updateIsAuthenticated(true));
                            dispatch(updateFirstName(userDetails.first_name));
                            dispatch(updateLastName(userDetails.last_name));
                            dispatch(updateEmailId(userDetails.email));
                            dispatch(updatePhoneNumber(userDetails.phone_no));
                            dispatch(updatePlanTierId(planTierId));
                            dispatch(updateCurrentPlanStartDate(new Date().toISOString()));
                            dispatch(updateUserId(userDetails.userId));
                            dispatch(updateStripeCustomerId(userDetails.stripeCustomerId));
                            dispatch(allowAccessToPaymentStatusPage());
                        }
                    } else {
                        dispatch(updatePlanTierId(planTierId)); // Replace with actual data
                        dispatch(updateCurrentPlanStartDate(new Date().toISOString()));
                        dispatch(allowAccessToPaymentStatusPage());
                    }
                    navigate('/payment-success');
                }
            } else {
                await updatePlan(emailId, planTierId);
                dispatch(updatePlanTierId(planTierId)); // Replace with actual data
                dispatch(updateCurrentPlanStartDate(new Date().toISOString()));
                dispatch(allowAccessToPaymentStatusPage());
                navigate('/payment-success');
            }
        } catch (err) {
            console.error("Error during subscription:", err);
            dispatch(allowAccessToPaymentStatusPage());
            navigate('/payment-failure', {
                state: {
                    errorMessage: "There was an error creating the subscription."
                },
            });
        } finally {
            setIsPaymentLoading(false);
        }
    };

    const handleDeletePaymentMethod = async (paymentMethodId) => {
        try {
            await deletePaymentMethod(stripeCustomerId, paymentMethodId);
            handleMenuClose();
            // Refresh the list after deletion
            loadPaymentMethods();
        } catch (error) {
            console.error("Error deleting payment method:", error);
        }
    };

    return (
        <div className='payment-page'>
            {isPaymentLoading ? 
            <div className="loader-container" style={{ height: "calc(100% - 60px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <div className="loader"></div>
                    <p>Subscribing...</p>
                </div> : (            
                    <div className="payment-page-container">
                         <div className="payment-content">
                            <div className='payment-methods-container'>
                            {isPaymentMethodLoading ? (
                                <div className="loader-container" style={{ height: "calc(100% - 60px)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <div className="loader"></div>
                                    <p>Payment methods loading...</p>
                                </div>
                            ) : (
                                <div className='payment-methods'>
                                    <table className="payment-methods-table">
                                    <thead>
                                        <tr>
                                            <th className="table-header-cards">Your Credit and Debit cards</th>
                                            <th>Name on card</th>
                                            <th>Expires on</th>
                                            <th>Manage</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!noPaymentMethodsAdded ? (
                                            paymentMethods.map((pm) => {
                                                const { brand, last4, exp_month, exp_year } = pm.card;
                                                const cardholderName = pm.billing_details?.name || 'User name';
                                                const brandKey = brand ? brand.toLowerCase() : 'unknown';
                                                const iconSrc = brandIcons[brandKey] || brandIcons["unknown"];

                                                return (
                                                <tr key={pm.id}>
                                                    <td>
                                                    <label className="radio-label">
                                                        <input
                                                        type="radio"
                                                        name="card"
                                                        value={pm.id}
                                                        checked={pm.id === selectedCardId}
                                                        onChange={() => setSelectedCardId(pm.id)}
                                                        />
                                                        <div className="card-info">
                                                        <div className="card-brand">
                                                            <img src={iconSrc} alt={brand} className="card-brand-icon" />
                                                        </div>
                                                        <div className="card-number">
                                                            {brand.charAt(0).toUpperCase() + brand.slice(1)} card ending in {last4}
                                                            {pm.id === defaultPaymentMethod && (
                                                                <span className="default-badge">Default</span>
                                                            )}
                                                        </div>
                                                        </div>
                                                    </label>
                                                    </td>
                                                    <td>{cardholderName}</td>
                                                    <td>
                                                    {String(exp_month).padStart(2, '0')}/{exp_year}
                                                    </td>
                                                    <td>
                                                    <IconButton  onClick={(event) => handleMenuOpen(event, pm.id)}>
                                                        <MoreVertIcon />
                                                    </IconButton>
                                                    {menuOpenFor === pm.id && (
                                                    <Menu anchorEl={anchorEl} open onClose={handleMenuClose}>
                                                        <MenuItem onClick={() => {makeDefaultPayment(pm.id); handleMenuClose();}} disabled={defaultPaymentMethod === pm.id}>
                                                        Make Default
                                                        </MenuItem>
                                                        <MenuItem onClick={() => {handleDeletePaymentMethod(pm.id);  handleMenuClose();}} disabled={defaultPaymentMethod === pm.id}>
                                                        Remove
                                                        </MenuItem>
                                                    </Menu>)}
                                                    </td>
                                                </tr>
                                                );
                                            })
                                        ) : (
                                        <tr>
                                            <td colSpan="4" style={{ textAlign: 'center', border: 'none', justifyContent: 'center', height: '100%' }}>
                                            No cards added
                                            </td>
                                        </tr>
                                        )}
                                    </tbody>
                                    </table>
                                </div> )}
                                
                            </div>
                            {!isPaymentMethodLoading && (
                                <div className='add-payment-method'>
                                    <span
                                        className="add-payment-plus"
                                        onClick={() => {
                                            addPaymentMethod()
                                        }}
                                    > + Add a credit or debit card. Prepitup accepts all major payment cards</span>
                                    {showAddCard && setupClientSecret && (
                                        <Elements stripe={stripePromise} options={{ clientSecret: setupClientSecret }}>
                                            <AddPaymentMethod
                                                clientSecret={setupClientSecret}
                                                onAddingCardComplete={handleAddingCardComplete}
                                                cancelClicked={() => setShowAddCard(false)}
                                            />
                                        </Elements>
                                    )}
                                </div>
                            )}
                            {!isPaymentMethodLoading && (
                                <div className='payment-methods-pay-option'>
                                    <div  className={`pay-button ${!selectedCardId ? 'disabled' : ''}`} onClick={handleSubscribe} disabled={true}>
                                        Subscribe
                                    </div>
                                    <div className='pay-terms'>
                                        By paying you are agreeing to prepitup terms and conditions.
                                    </div>
                                    <div className='payment-breakdown'>
                                        <div className='item-price'>Item: ${itemPrice}</div>
                                        <div className='item-price'>Credits Applied: ${creditsUsed}</div>
                                        <div className='item-price'>Total Payable: ${total}</div>
                                    </div>
                                </div>
                            )}

                        </div>
                        <div className="payment-image">
                            <img className="payment-icon" src={paymentIcon} alt="icon" />
                        </div>
                    </div>)}
        </div>
    );
};

export default PaymentPage;